/**
 * Turns what platform-api hands to `sharedHostGateway.logger.error` into one widget load
 * failure, and reads the page context a report carries.
 *
 * Three call sites in `@invent/platform-api` feed the logger, all with the same shape:
 * `error(String(error), { message, stack?, tags: { origin, environment, scope, url } })`
 * from `RemoteModule` (module registry says `failed`), `ErrorBoundaryWidgetComponent`
 * (a thrown ChunkLoadError or render error) and `useRemoteModuleContent`. `tags.scope` is
 * the widget name, `tags.url` its `remoteEntry.js`.
 */

import { double, type DoubleValue } from './otlp';

export interface WidgetLoadFailure {
  scope: string;
  /** The remote entry as origin + path; see `normalizeRemoteUrl`. */
  url: string;
  message: string;
  stack?: string;
  /** The error class (`ChunkLoadError`, `TypeError`), when a real error was passed. */
  type?: string;
  /** The dashboard widget instance, when the widget is a tile (platform-api 1.25.0 and later). */
  instanceId?: string;
  /**
   * Whole milliseconds between the start of the widget slot's wait and the failure, as
   * platform-api 1.24.0 and later measure it (`elapsedMs`). A chunk that ran into the
   * bundler's timeout shows about 120 000, a script blocked outright about 0; for a render
   * error (`type` other than `ChunkLoadError`) it is the widget's age. Absent when the
   * reporting instance never waited, e.g. a second instance of an already failed widget.
   */
  elapsedMs?: number;
}

interface LoggerContext {
  message?: unknown;
  stack?: unknown;
  elapsedMs?: unknown;
  tags?: unknown;
}

/** What `String(error)` yields when RemoteModule stringifies the registry's error object. */
const OBJECT_TO_STRING = '[object Object]';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** A finite, non-negative number, rounded; anything else is not a measurement. */
function asMilliseconds(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : undefined;
}

/** `Error.prototype.toString()` yields `"<name>: <message>"`; the name is the class. */
const ERROR_PREFIX = /^((?:[A-Za-z_$][\w$]*)?Error)\b/;

function errorType(error: unknown): string | undefined {
  if (error instanceof Error) {
    return asString(error.name);
  }
  const text = asString(error);
  return text === undefined ? undefined : ERROR_PREFIX.exec(text)?.[1];
}

function errorMessage(error: unknown): string | undefined {
  if (error instanceof Error) {
    return asString(error.message);
  }
  if (isRecord(error)) {
    return asString(error.message);
  }
  const text = asString(error);
  return text === OBJECT_TO_STRING ? undefined : text;
}

/**
 * A remote url reduced to origin + path: no query string, no fragment. A signed or
 * cache-busted CDN url must not put its token into a log record, and two attempts at the
 * same widget with different cache busters are one failure, not two (the dedup key is
 * this value).
 */
export function normalizeRemoteUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split(/[?#]/, 1)[0] ?? url;
  }
}

/**
 * A url followed by a query string or fragment; a trailing `:line:column` (a stack frame)
 * is kept, it is the one part of the tail that helps.
 */
const URL_WITH_QUERY = /(https?:\/\/[^\s?#'"()]+)[?#][^\s'"()]*?((?::\d+){2})?(?=[\s'"()]|$)/g;

/** Strips query strings and fragments from every url inside a message or stack trace. */
export function stripUrlQueries(text: string): string {
  return text.replace(URL_WITH_QUERY, '$1$2');
}

/**
 * `undefined` when the call carries neither scope nor url: whatever it is, it is not a
 * widget load failure, and this reporter only knows how to describe those.
 */
export function normalizeFailure(args: unknown[]): WidgetLoadFailure | undefined {
  const [error, context] = args;
  const ctx: LoggerContext = isRecord(context) ? context : {};
  const tags = isRecord(ctx.tags) ? ctx.tags : {};
  const scope = asString(tags.scope);
  const url = asString(tags.url);
  if (!scope && !url) {
    return undefined;
  }

  const stack = asString(ctx.stack) ?? (error instanceof Error ? asString(error.stack) : undefined);
  return {
    scope: scope ?? '',
    url: url === undefined ? '' : normalizeRemoteUrl(url),
    message: stripUrlQueries(asString(ctx.message) ?? errorMessage(error) ?? 'unknown error'),
    stack: stack === undefined ? undefined : stripUrlQueries(stack),
    type: errorType(error),
    elapsedMs: asMilliseconds(ctx.elapsedMs),
    instanceId: asString(tags.instanceId)
  };
}

export interface RemoteEntry {
  widgetId?: string;
  version?: string;
}

/**
 * Widget id and version from the remote host that `generateWidgetData` built:
 * `<store>/widgets/<id>/<version>/remoteEntry.js`, or `<store>/<id>/remoteEntry.js` for
 * stores predating version control. A localhost dev-federation url yields nothing.
 */
export function parseRemoteEntry(url: string): RemoteEntry {
  let segments: string[];
  try {
    segments = new URL(url).pathname.split('/').filter(Boolean);
  } catch {
    return {};
  }
  if (segments[segments.length - 1] !== 'remoteEntry.js') {
    return {};
  }
  if (segments.length === 4 && segments[0] === 'widgets') {
    return { widgetId: segments[1], version: segments[2] };
  }
  if (segments.length === 2) {
    return { widgetId: segments[0] };
  }
  return {};
}

/** Same cap saas-bff applies to span attributes, same mark. */
export const MAX_ATTRIBUTE_LENGTH = 512;

export function truncate(value: string | undefined): string | undefined {
  if (value === undefined || value.length <= MAX_ATTRIBUTE_LENGTH) {
    return value;
  }
  return `${value.slice(0, MAX_ATTRIBUTE_LENGTH)}…[truncated]`;
}

/**
 * `deployment.environment.name`, decided the way `src/main.tsx` picks its bootstrap URL.
 * Same words as the backend services use in their OTEL_RESOURCE_ATTRIBUTES.
 */
export function environmentName(
  origin: string = window.location.origin,
  appEnv: string | undefined = window.APP_ENV
): string {
  if (origin.includes('localhost')) {
    return 'local';
  }
  if (origin.includes('.sandbox.apptrium.cloud') || appEnv === 'DEV') {
    return 'development';
  }
  if (origin.includes('.rc.apptrium.cloud') || appEnv === 'UAT') {
    return 'uat';
  }
  return 'production';
}

const COMMIT_HASH = /^[0-9a-f]{6,40}$/i;

/**
 * The short commit hash docker-entrypoint.sh writes into index.html. Undefined when the
 * placeholder was never replaced (development, or a broken deploy).
 */
export function buildVersion(root: ParentNode = document): string | undefined {
  const value = root.querySelector<HTMLInputElement>(
    'input[name="Build commit hash short"]'
  )?.value;
  return value && COMMIT_HASH.test(value) ? value : undefined;
}

/**
 * The portal's project id: `TENENT_KEY` in the bootstrap response is the same
 * `widgetStoreProjectId` that saas-bff records as `portal.id`, so the browser record joins
 * the backend ones on the same key. Read at report time, not at module load: in development
 * the bootstrap response reaches `window` only after startup.
 */
export function portalId(): string | undefined {
  return asString(window.TENENT_KEY);
}

/** The Network Information API, as Chromium exposes it; other engines have none of it. */
interface NetworkInformationLike {
  type?: unknown;
  effectiveType?: unknown;
  downlink?: unknown;
  rtt?: unknown;
  saveData?: unknown;
}

export interface NetworkContext {
  'network.connection.type'?: string;
  'network.connection.effective_type'?: string;
  /** Fractional by nature (25 kbps steps), so a double even at the cap of 10; see `otlp.ts`. */
  'network.connection.downlink_mbps'?: DoubleValue;
  /** Whole milliseconds: the browser rounds to 25 ms steps. */
  'network.connection.rtt_ms'?: number;
  'network.connection.save_data'?: boolean;
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asDouble(value: unknown): DoubleValue | undefined {
  const finite = asFiniteNumber(value);
  return finite === undefined ? undefined : double(finite);
}

/**
 * What the browser knows about the connection at report time, so a slow-network timeout
 * can be told from a CDN that answered quickly with an error. Chromium only: `type` is the
 * interface (`wifi`, `cellular`, ...), `effectiveType` a `4g` / `3g` / `2g` / `slow-2g`
 * class computed from recent rtt and throughput, `downlink` in Mbps and `rtt` in ms, both
 * rounded by the browser (25 kbps and 25 ms steps, downlink capped at 10) on purpose, so
 * they identify nobody. Empty where the API is missing (Safari, Firefox).
 */
export function networkInformation(
  connection: NetworkInformationLike | undefined = (
    navigator as Navigator & { connection?: NetworkInformationLike }
  ).connection
): NetworkContext {
  if (connection === undefined || connection === null || typeof connection !== 'object') {
    return {};
  }
  return {
    'network.connection.type': asString(connection.type),
    'network.connection.effective_type': asString(connection.effectiveType),
    'network.connection.downlink_mbps': asDouble(connection.downlink),
    'network.connection.rtt_ms': asFiniteNumber(connection.rtt),
    'network.connection.save_data':
      typeof connection.saveData === 'boolean' ? connection.saveData : undefined
  };
}
